import { CubePresenter, FlipIntent, Logger, NOOP_PROMISE_VOID, Presenter, type IPresenterOwner } from 'wdc-cube'

import * as hash from 'object-hash'
import lodash from 'src/utils/lodash'

import { ParamIds, Places } from 'src/Constants'
import { TheConsumerMarketKeys } from '../tcm_keys'

import { MainPresenter } from 'src/main/Main.presenter'
import { GroupManagerService } from 'src/modules/admin/group-manager/gm_service'
import { UserManagerService } from 'src/modules/admin/user-manager/um_service'
import { AppStorage } from 'src/utils'
import { describeFilterFields } from '../tcm_filter_field_labels'
import {
    FilterCatalogFormScope,
    FilterCatalogScope,
    FilterCatalogShareScope,
    type FilterCatalogFormMode,
    type FilterCatalogShareMode,
    type FilterCatalogTab
} from '../tcm_scopes'
import { EmpresaFilter, TheConsumerMarketService, type FilterCatalogOption } from '../tcm_service'
import { type TcmPresenterForFilterPane } from './tcm_presenter_filter_pane'

const LOG = Logger.get('FilterCatalogPresenter')

const appStorage = AppStorage.singleton()

const service = TheConsumerMarketService.singleton()
const userManagerService = UserManagerService.singleton()
const groupManagerService = GroupManagerService.singleton()

class ForbiddenError extends Error {}

function signatureOf(filter: EmpresaFilter): string {
    return hash.MD5(JSON.stringify(filter ?? {}))
}

function describeOrigin(option: FilterCatalogOption): string {
    if (option.owner) {
        return option.sharedCount > 0
            ? `${option.sharedCount} ${option.sharedCount > 1 ? 'destinatários' : 'destinatário'}`
            : 'só você'
    }
    return option.ownerName ? `por ${option.ownerName}` : 'compartilhado com você'
}

export type FilterCatalogHost = IPresenterOwner & {
    readonly scope: { readonly catalog: FilterCatalogScope }
    apply(filter: EmpresaFilter): Promise<void>
}

export type FilterCatalogConfig = {
    app: MainPresenter
    module: string
    buildData(): EmpresaFilter
    openForm(mode: FilterCatalogFormMode, sourceId: string): Promise<unknown>
    openShare(sourceId: string): Promise<unknown>
}

function tcmCatalogConfig(pane: TcmPresenterForFilterPane): FilterCatalogConfig {
    const app = pane.owner.app
    return {
        app,
        module: 'tcm',
        buildData: () => pane.filterManager.build(),
        openForm: (mode, sourceId) => new FilterCatalogFormKeys(app).setMode(mode).setSourceId(sourceId).flip(),
        openShare: (sourceId) => new FilterCatalogShareKeys(app).setSourceId(sourceId).flip()
    }
}

export class TcmPresenterForFilterCatalog extends Presenter<FilterCatalogScope, FilterCatalogHost> {
    // prettier-ignore
    constructor(owner: FilterCatalogHost, config?: FilterCatalogConfig) {
        super(owner, owner.scope.catalog)
        this.config = config ?? tcmCatalogConfig(owner as TcmPresenterForFilterPane)
        this.app = this.config.app
    }

    readonly app: MainPresenter
    readonly config: FilterCatalogConfig

    private _options: FilterCatalogOption[] = []
    private _optionByIdMap = new Map<string, FilterCatalogOption>()
    private _yesAction = NOOP_PROMISE_VOID

    private _openedSignature = ''

    get options(): FilterCatalogOption[] {
        return this._options
    }

    buildData(): EmpresaFilter {
        return this.config.buildData()
    }

    get openedOption(): FilterCatalogOption | undefined {
        return this.scope.openedId ? this._optionByIdMap.get(this.scope.openedId) : undefined
    }

    async prepare() {
        this.scope.getOptions = () => this._options
        this.scope.getVisibleOptions = this._getVisibleOptions.bind(this)
        this.scope.getFieldLabels = (option) => describeFilterFields(option.fieldKeys)
        this.scope.countByTab = this._countByTab.bind(this)

        this.scope.onToggleLibrary = this.action(this._handleToggleLibrary)
        this.scope.onTabChange = this.action(this._handleTabChange)
        this.scope.onSearchChange = this.action(this._handleSearchChange)
        this.scope.onToggleExpanded = this.action(this._handleToggleExpanded)
        this.scope.onToggleGroup = this.action(this._handleToggleGroup)
        this.scope.onToggleFavorite = this.action(this._handleToggleFavorite)

        this.scope.onApplyFilter = this.action(this._handleApplyFilter)
        this.scope.onCloseOpened = this.action(this._handleCloseOpened)
        this.scope.onSaveOpened = this.action(this._handleSaveOpened)
        this.scope.onDiscardChanges = this.action(this._handleDiscardChanges)

        this.scope.onCreate = this.action(this._handleCreate)
        this.scope.onSaveAsNew = this.action(this._handleSaveAsNew)
        this.scope.onDuplicate = this.action(this._handleDuplicate)
        this.scope.onRename = this.action(this._handleRename)
        this.scope.onShare = this.action(this._handleShare)
        this.scope.onDelete = this.action(this._handleDelete)
        this.scope.onYesNoResponse = this.action(this._handleYesNoResponse)
    }

    release(): void {
        // Nada a liberar: o escopo pertence ao painel de filtros.
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async applyParameters(keys: { readonly last: boolean }, _initialization: boolean) {
        if (!keys.last) {
            this.scope.askYesOrNo = ''
            this._yesAction = NOOP_PROMISE_VOID
        }
    }

    async updateOptions() {
        this._options = await service.fetchSavedFilters(this.config.module)
        this._options.sort((a, b) => {
            const byGroup = a.group.localeCompare(b.group)
            if (byGroup !== 0) {
                return byGroup
            }
            return a.title.localeCompare(b.title)
        })

        this._optionByIdMap.clear()
        this._options.forEach((option) => {
            this._optionByIdMap.set(option.id, option)
        })

        this._refreshOpenedDescription()
        this.scope.update()
    }

    private async _reloadOptions() {
        this.scope.loading = true
        try {
            await this.updateOptions()
        } catch (caught) {
            LOG.caught(caught)
            this.app.alert('error', 'Acesso aos dados', 'Não foi possível buscar os filtros salvos no servidor')
        } finally {
            this.scope.loading = false
        }
    }

    refreshDirtyState() {
        if (!this.scope.openedId) {
            if (this.scope.dirty) {
                this.scope.dirty = false
            }
            return
        }

        const dirty = signatureOf(this.buildData()) !== this._openedSignature
        if (dirty !== this.scope.dirty) {
            this.scope.dirty = dirty
        }
    }

    private _markOpened(option: FilterCatalogOption) {
        this.scope.openedId = option.id
        this.scope.openedTitle = option.title
        this.scope.openedGroup = option.group
        this.scope.openedOwner = option.owner
        this.scope.openedSummary = describeOrigin(option)
        this._rebaseOpenedSignature()
    }

    private _refreshOpenedDescription() {
        const opened = this.openedOption
        if (!opened) {
            return
        }
        this.scope.openedTitle = opened.title
        this.scope.openedGroup = opened.group
        this.scope.openedOwner = opened.owner
        this.scope.openedSummary = describeOrigin(opened)
    }

    private _rebaseOpenedSignature() {
        this._openedSignature = signatureOf(this.buildData())
        this.scope.dirty = false
    }

    private _countByTab(tab: FilterCatalogTab): number {
        return this._options.filter((option) => this._matchesTab(option, tab)).length
    }

    private _matchesTab(option: FilterCatalogOption, tab: FilterCatalogTab): boolean {
        switch (tab) {
            case 'mine':
                return option.owner
            case 'shared':
                return !option.owner
            case 'fav':
                return option.favorite
            default:
                return true
        }
    }

    private _getVisibleOptions(): FilterCatalogOption[] {
        const search = this.scope.searchText.trim().toLowerCase()

        return this._options.filter((option) => {
            if (!this._matchesTab(option, this.scope.tab)) {
                return false
            }

            if (!search) {
                return true
            }

            const haystack = [option.title, option.group, option.ownerName ?? '']
                .concat(describeFilterFields(option.fieldKeys))
                .join(' ')
                .toLowerCase()

            return haystack.indexOf(search) !== -1
        })
    }

    protected async _handleToggleLibrary(open: boolean) {
        this.scope.libraryOpen = open
        if (open) {
            await this._reloadOptions()
        }
    }

    protected async _handleTabChange(tab: string) {
        this.scope.tab = tab as FilterCatalogTab
    }

    protected async _handleSearchChange(text: string) {
        this.scope.searchText = text
    }

    protected async _handleToggleExpanded(id: string) {
        const expanded = this.scope.expandedIds
        this.scope.expandedIds = expanded.indexOf(id) === -1 ? [...expanded, id] : expanded.filter((it) => it !== id)
    }

    protected async _handleToggleGroup(key: string) {
        const expanded = this.scope.expandedGroups
        this.scope.expandedGroups =
            expanded.indexOf(key) === -1 ? [...expanded, key] : expanded.filter((it) => it !== key)
    }

    protected async _handleToggleFavorite(option: FilterCatalogOption) {
        const favorite = !option.favorite

        option.favorite = favorite
        this.scope.update()

        try {
            await service.favoriteFilter(option.id, option.ownerId, favorite)
        } catch (caught) {
            LOG.caught(caught)
            option.favorite = !favorite
            this.scope.update()
            this.app.alert('error', 'Favoritos', 'Não foi possível atualizar o favorito. Tente mais tarde')
        }
    }

    protected async _handleApplyFilter(filterId: string) {
        const { app } = this

        const selectedOption = this._optionByIdMap.get(filterId)
        if (!selectedOption) {
            return
        }

        const remoteEmpFilter = await service.fetchFilterDataById(filterId)
        if (!remoteEmpFilter) {
            app.alert('error', 'Filtro não encontrado', 'Por algum problema interno o filtro não foi localizado')
            return
        }

        await this.owner.apply(remoteEmpFilter)

        this._markOpened(selectedOption)
        this.scope.libraryOpen = false
        this.scope.update()

        app.notify('success', `${selectedOption.title} aplicado`)
    }

    protected async _handleCloseOpened() {
        this.scope.openedId = ''
        this.scope.openedTitle = ''
        this.scope.openedGroup = ''
        this.scope.openedOwner = false
        this.scope.openedSummary = ''
        this.scope.dirty = false
        this._openedSignature = ''
    }

    protected async _handleDiscardChanges() {
        const opened = this.openedOption
        if (!opened) {
            return
        }

        const remoteEmpFilter = await service.fetchFilterDataById(opened.id)
        if (!remoteEmpFilter) {
            this.app.alert('error', 'Filtro não encontrado', 'Não foi possível recuperar a versão salva')
            return
        }

        await this.owner.apply(remoteEmpFilter)
        this._markOpened(opened)
        this.scope.update()

        this.app.notify('info', 'Alterações descartadas')
    }

    protected async _handleSaveOpened() {
        const opened = this.openedOption
        if (!opened || !opened.owner) {
            return
        }

        const filter = this.buildData()
        if (lodash.isEmpty(filter)) {
            this.app.alert('error', 'Valor inválido', 'Não há filtros informados')
            return
        }

        try {
            await service.saveFilter({
                id: opened.id,
                module: this.config.module,
                title: opened.title,
                group: opened.group,
                groups: opened.groups,
                users: opened.users,
                data: filter
            })

            this._rebaseOpenedSignature()
            await this._reloadOptions()

            const recipients = opened.sharedCount
            this.app.notify(
                'success',
                recipients > 0
                    ? `Filtro atualizado — ${recipients} ${
                          recipients > 1 ? 'destinatários veem' : 'destinatário vê'
                      } a nova versão`
                    : 'Filtro atualizado'
            )
        } catch (caught) {
            LOG.caught(caught)
            this.app.alert('error', 'Impossível salvar', (caught as Error).message ?? 'Erro interno')
        }
    }

    protected async _handleCreate() {
        await this._openForm('create')
    }

    protected async _handleSaveAsNew() {
        await this._openForm('save-as-new', this.openedOption)
    }

    protected async _handleDuplicate(option: FilterCatalogOption) {
        await this._openForm('duplicate', option)
    }

    protected async _handleRename(option: FilterCatalogOption) {
        await this._openForm('rename', option)
    }

    private async _openForm(mode: FilterCatalogFormMode, option?: FilterCatalogOption) {
        this.scope.libraryOpen = false
        try {
            await this.config.openForm(mode, option?.id ?? '')
        } catch (caught) {
            if (caught instanceof ForbiddenError) {
                this.app.alert('info', 'Acesso impedido', `${(caught as ForbiddenError).message}`)
            } else {
                throw caught
            }
        }
    }

    protected async _handleShare(option: FilterCatalogOption) {
        if (!option.owner) {
            this.app.alert('info', 'Acesso impedido', 'Só o dono do filtro pode compartilhá-lo')
            return
        }

        this.scope.libraryOpen = false
        await this.config.openShare(option.id)
    }

    protected async _handleDelete(option: FilterCatalogOption) {
        if (!option.owner) {
            this.app.alert('info', 'Acesso impedido', 'Só o dono do filtro pode excluí-lo')
            return
        }

        const impact =
            option.sharedCount > 0
                ? ` ${option.sharedCount} ${
                      option.sharedCount > 1 ? 'destinatários usam' : 'destinatário usa'
                  } este filtro e vai perder o acesso.`
                : ''

        this.scope.askYesOrNo = `Excluir "${option.title}"? Esta ação não pode ser desfeita.${impact}`

        this._yesAction = async () => {
            try {
                await service.deleteFilterById(option.id)
                if (this.scope.openedId === option.id) {
                    await this._handleCloseOpened()
                }
                await this._reloadOptions()
                this._yesAction = NOOP_PROMISE_VOID
                this.app.notify('success', 'Filtro excluído')
            } catch (error) {
                LOG.caught(error)
                this.app.alert('error', 'Problemas', 'Não foi possível excluir o registro. Tente mais tarde')
            }
        }
    }

    protected async _handleYesNoResponse(yes: boolean) {
        this.scope.askYesOrNo = ''
        if (yes) {
            await this._yesAction()
        }
        this.scope.update()
    }

    async onFilterSaved(id: string, title: string, group: string, keepOpened: boolean) {
        await this._reloadOptions()

        const saved = this._optionByIdMap.get(id)
        if (keepOpened && saved) {
            this._markOpened(saved)
        } else {
            this._refreshOpenedDescription()
        }

        this.scope.update()
        this.app.notify('success', `"${title}" salvo na pasta ${group}`)
    }

    async onFilterShared(title: string) {
        await this._reloadOptions()
        this.scope.update()
        this.app.notify('success', `Compartilhamento de "${title}" atualizado`)
    }
}

// :: Formulário de criação / duplicação / renomeação

export class FilterCatalogFormKeys extends TheConsumerMarketKeys {
    get place() {
        return Places.theConsumerMarket_FilterCatalogForm
    }

    setMode(mode: FilterCatalogFormMode) {
        this._intent.setParameter(ParamIds.tcmFilterFormMode, mode)
        return this
    }

    get mode(): FilterCatalogFormMode {
        return (this._intent.getParameterAsString(ParamIds.tcmFilterFormMode) ?? 'create') as FilterCatalogFormMode
    }

    setSourceId(id: string) {
        this._intent.setParameter(ParamIds.tcmFilterSourceId, id)
        return this
    }

    get sourceId(): string {
        return this._intent.getParameterAsString(ParamIds.tcmFilterSourceId) ?? ''
    }
}

export type FilterCatalogDialogContext = {
    mode: FilterCatalogFormMode
    sourceId: string
    catalog: TcmPresenterForFilterCatalog
    dialogSlot(scope: FilterCatalogFormScope | FilterCatalogShareScope): void
}

export class TcmPresenterForFilterCatalogForm extends CubePresenter<MainPresenter, FilterCatalogFormScope> {
    constructor(app: MainPresenter) {
        super(app, new FilterCatalogFormScope())
    }

    protected resolveContext(intent: FlipIntent): FilterCatalogDialogContext {
        const keys = new FilterCatalogFormKeys(this.app, intent)
        return {
            mode: keys.mode,
            sourceId: keys.sourceId,
            catalog: keys.tcmPresenter.filterPane.catalogFilter,
            dialogSlot: (scope) => keys.dialogSlot(scope)
        }
    }

    protected async flipBack() {
        await new TheConsumerMarketKeys(this.app).flip()
    }

    protected _catalogFilter!: TcmPresenterForFilterCatalog
    protected _optionByGroupMap = new Map<string, FilterCatalogOption[]>()
    protected _filter: EmpresaFilter = {}
    protected _source?: FilterCatalogOption

    release(): void {
        if (this.app.scope.dialog === this.scope) {
            this.app.scope.dialog = undefined
        }
        super.release()
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean) {
        const keys = this.resolveContext(intent)
        if (initialization) {
            this._catalogFilter = keys.catalog

            this.scope.onClose = this.action(this._handleOnClose)
            this.scope.onSave = this.action(this._handleOnSave)
            this.scope.onTitleChange = this.action(this._handleTitleChange)
            this.scope.onGroupChange = this.action(this._handleGroupChange)
            this.scope.onShareModeChange = this.action(this._handleShareModeChange)
            this.scope.onGroupsChange = this.action(this._handleGroupsChange)
            this.scope.onUsersChange = this.action(this._handleUsersChange)

            this.scope.mode = keys.mode
            this._source = this._catalogFilter.options.find((it) => it.id === keys.sourceId)

            if (this.scope.mode !== 'rename') {
                this._filter = this._catalogFilter.buildData()
                if (lodash.isEmpty(this._filter)) {
                    throw new ForbiddenError('Nenhum filtro foi informado no painel')
                }
            }

            await this._populateOptions()
            await this._populateAllUsersOptions()
            await this._populateAllGroupsOptions()
            this._populateInitialValues()
        }

        keys.dialogSlot(this.scope)

        return true
    }

    private _populateInitialValues() {
        const source = this._source

        switch (this.scope.mode) {
            case 'rename':
                this.scope.title = source?.title ?? ''
                this.scope.group = source?.group ?? ''
                break
            case 'duplicate':
                this.scope.title = source ? `${source.title} (cópia)` : ''
                this.scope.group = source?.group ?? ''
                break
            case 'save-as-new':
                this.scope.title = source ? `${source.title} (novo)` : ''
                this.scope.group = source?.group ?? ''
                break
            default:
                this.scope.title = ''
                this.scope.group = ''
                break
        }

        this.scope.groups = this.scope.mode === 'rename' ? (source?.groups ?? []) : []
        this.scope.users = this.scope.mode === 'rename' ? (source?.users ?? []) : []
        if (this.scope.users.length > 0) {
            this.scope.shareMode = 'pick'
        } else if (this.scope.groups.length > 0) {
            this.scope.shareMode = 'dept'
        } else {
            this.scope.shareMode = 'me'
        }

        this._refreshDuplicateWarning()
    }

    protected async _populateAllUsersOptions() {
        if (appStorage.userManagerEnabled && (!this.scope.allUsers || this.scope.allUsers.length === 0)) {
            this.scope.allUsers = await userManagerService.fetchUsers()
        }
    }

    protected async _populateAllGroupsOptions() {
        if (!this.scope.allGroups || this.scope.allGroups.length === 0) {
            if (appStorage.userManagerEnabled) {
                this.scope.allGroups = await groupManagerService.fetchGroups()
            } else {
                this.scope.allGroups = await groupManagerService.fetchAdministeredGroups()
                this.scope.allUsers = this.scope.allGroups?.flatMap((g) => g.users || []) || []
            }
        }
    }

    protected async _populateOptions() {
        if (this._catalogFilter.options.length === 0) {
            await this._catalogFilter.updateOptions()
        }

        this._optionByGroupMap.clear()

        this._catalogFilter.options.forEach((option) => {
            if (!option.owner) {
                return
            }
            const bucket = this._optionByGroupMap.get(option.group)
            if (bucket) {
                bucket.push(option)
            } else {
                this._optionByGroupMap.set(option.group, [option])
            }
        })

        this.scope.groupOptions = [...this._optionByGroupMap.keys()].sort((a, b) => a.localeCompare(b))
    }

    private _findConflict(): FilterCatalogOption | undefined {
        const group = this.scope.group.trim()
        const title = this.scope.title.trim()
        if (!group || !title) {
            return undefined
        }

        return (this._optionByGroupMap.get(group) ?? []).find(
            (option) => option.title === title && option.id !== this._source?.id
        )
    }

    private _refreshDuplicateWarning() {
        const conflict = this._findConflict()
        this.scope.duplicateOf = conflict ? conflict.id : ''
    }

    protected async _handleTitleChange(title: string) {
        this.scope.title = title
        this._refreshDuplicateWarning()
    }

    protected async _handleGroupChange(group: string) {
        this.scope.group = group
        this._refreshDuplicateWarning()
    }

    protected async _handleShareModeChange(mode: string) {
        this.scope.shareMode = mode as FilterCatalogShareMode

        if (this.scope.shareMode === 'me') {
            this.scope.groups = []
            this.scope.users = []
            return
        }

        if (this.scope.shareMode === 'dept') {
            this.scope.users = []
            const departments = this.scope.allGroups ?? []
            if (departments.length === 1 && departments[0].id) {
                this.scope.groups = [departments[0].id]
            }
        }
    }

    protected async _handleGroupsChange(groups: number[]) {
        this.scope.groups = groups
    }

    protected async _handleUsersChange(users: number[]) {
        this.scope.users = users
    }

    protected async _handleOnClose() {
        await this.flipBack()
    }

    protected async _handleOnSave() {
        const { app, scope } = this

        const group = scope.group.trim()
        const title = scope.title.trim()

        if (!group) {
            app.alert('error', 'Valor inválido', 'A pasta precisa ser informada')
            return
        }

        if (!title) {
            app.alert('error', 'Valor inválido', 'O nome do filtro precisa ser informado')
            return
        }

        try {
            if (scope.mode === 'rename') {
                if (!this._source) {
                    app.alert('error', 'Filtro não encontrado', 'O filtro a renomear não está mais disponível')
                    return
                }

                await service.renameFilter({ id: this._source.id, title, group })
                await this._catalogFilter.onFilterSaved(
                    this._source.id,
                    title,
                    group,
                    this._catalogFilter.scope.openedId === this._source.id
                )
            } else {
                const conflict = this._findConflict()

                const savedId = await service.saveFilter({
                    id: conflict?.id,
                    module: this._catalogFilter.config.module,
                    title,
                    group,
                    groups: scope.groups,
                    users: scope.users,
                    data: this._filter
                })

                await this._catalogFilter.onFilterSaved(savedId, title, group, scope.mode !== 'duplicate')
            }

            await this.flipBack()
        } catch (caught) {
            LOG.caught(caught)
            const exn = caught as Record<string, unknown>
            app.alert('error', 'Impossível salvar', exn.message ? `${exn.message}` : 'Erro interno')
        }
    }
}

export class FilterCatalogShareKeys extends TheConsumerMarketKeys {
    get place() {
        return Places.theConsumerMarket_FilterCatalogShare
    }

    setSourceId(id: string) {
        this._intent.setParameter(ParamIds.tcmFilterSourceId, id)
        return this
    }

    get sourceId(): string {
        return this._intent.getParameterAsString(ParamIds.tcmFilterSourceId) ?? ''
    }
}

export class TcmPresenterForFilterCatalogShare extends CubePresenter<MainPresenter, FilterCatalogShareScope> {
    constructor(app: MainPresenter) {
        super(app, new FilterCatalogShareScope())
    }

    protected resolveContext(intent: FlipIntent): FilterCatalogDialogContext {
        const keys = new FilterCatalogShareKeys(this.app, intent)
        return {
            mode: 'create',
            sourceId: keys.sourceId,
            catalog: keys.tcmPresenter.filterPane.catalogFilter,
            dialogSlot: (scope) => keys.dialogSlot(scope)
        }
    }

    protected async flipBack() {
        await new TheConsumerMarketKeys(this.app).flip()
    }

    protected _catalogFilter!: TcmPresenterForFilterCatalog
    protected _source?: FilterCatalogOption

    release(): void {
        if (this.app.scope.dialog === this.scope) {
            this.app.scope.dialog = undefined
        }
        super.release()
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean) {
        const keys = this.resolveContext(intent)
        if (initialization) {
            this._catalogFilter = keys.catalog

            this.scope.onClose = this.action(this._handleOnClose)
            this.scope.onSave = this.action(this._handleOnSave)
            this.scope.onGroupsChange = this.action(this._handleGroupsChange)
            this.scope.onUsersChange = this.action(this._handleUsersChange)

            this._source = this._catalogFilter.options.find((it) => it.id === keys.sourceId)
            if (!this._source) {
                throw new ForbiddenError('O filtro não está mais disponível')
            }

            this.scope.title = this._source.title
            this.scope.groups = [...this._source.groups]
            this.scope.users = [...this._source.users]

            await this._populateOptions()

            this.scope.groupName = (id) => this.scope.allGroups?.find((g) => g.id === id)?.name ?? String(id)
            this.scope.userName = (id) => {
                const user = this.scope.allUsers?.find((u) => u.id === id)
                return user?.full_name || user?.name || String(id)
            }
        }

        keys.dialogSlot(this.scope)

        return true
    }

    private async _populateOptions() {
        if (appStorage.userManagerEnabled) {
            this.scope.allGroups = await groupManagerService.fetchGroups()
            this.scope.allUsers = await userManagerService.fetchUsers()
        } else {
            this.scope.allGroups = await groupManagerService.fetchAdministeredGroups()
            this.scope.allUsers = this.scope.allGroups?.flatMap((g) => g.users || []) || []
        }
    }

    protected async _handleGroupsChange(groups: number[]) {
        this.scope.groups = groups
    }

    protected async _handleUsersChange(users: number[]) {
        this.scope.users = users
    }

    protected async _handleOnClose() {
        await this.flipBack()
    }

    protected async _handleOnSave() {
        if (!this._source) {
            return
        }

        try {
            await service.shareFilter({
                id: this._source.id,
                groups: this.scope.groups,
                users: this.scope.users
            })

            await this._catalogFilter.onFilterShared(this._source.title)
            await this.flipBack()
        } catch (caught) {
            LOG.caught(caught)
            const exn = caught as Record<string, unknown>
            this.app.alert('error', 'Impossível compartilhar', exn.message ? `${exn.message}` : 'Erro interno')
        }
    }
}
